import { useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  Stripe,
  StripeElements,
  StripeError,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent
} from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { digitalWalletErrors, getErrorFromStripeErrorCode } from './digital-wallet-errors';
import type { DisplayError } from './digital-wallet-errors';
import { useDigitalWalletsEventTracking } from './use-digital-wallets-event-tracking';
import { usePaymentContext } from '../../../contexts/payment';
import { completeDigitalWalletPayment, createDigitalWalletPaymentIntent } from '../../../services';
import type {
  DigitalWallet,
  PurchasePaymentBreakdown
} from '../../../services/purchase-payments-api.types';
import type { CompletePurchaseSubmitResult } from '../../../use-complete-purchase-vm';
import { getDigitalWalletLineItems } from '../utils';

const supportCheckTimeoutMs = 10000;

const getStripeExpressCheckoutElementOptions = (
  digitalWallet: DigitalWallet | undefined,
  paymentType: string,
  purchasePaymentBreakdown: PurchasePaymentBreakdown
) =>
  ({
    paymentMethods: {
      applePay: digitalWallet === 'GooglePay' ? 'never' : 'always',
      googlePay: digitalWallet === 'ApplePay' ? 'never' : 'always',
      amazonPay: 'never',
      klarna: 'never',
      link: 'never',
      paypal: 'never'
    },
    paymentMethodOrder: ['apple_pay', 'google_pay'],
    buttonType: {
      applePay: 'plain',
      googlePay: 'plain'
    },
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black'
    },
    layout: {
      maxColumns: 1,
      overflow: 'never'
    },
    lineItems: getDigitalWalletLineItems(paymentType, purchasePaymentBreakdown),
    buttonHeight: 50
  }) satisfies StripeExpressCheckoutElementOptions;

interface DigitalWalletPaymentIntent {
  paymentId: string;
  paymentIntentId: string;
  clientSecret: string;
}

type PrepareDigitalWalletPaymentResult =
  | { paymentIntent: DigitalWalletPaymentIntent; error?: undefined }
  | { error: DisplayError; paymentIntent?: undefined };

type ConfirmDigitalWalletPaymentResult =
  | { status: 'PaymentCollected'; error?: undefined }
  | { status: 'PurchaseRolledBack'; error?: undefined }
  | { status: 'PaymentFailed'; error: DisplayError };

type CollectDigitalWalletPaymentResult = { keepSpinner: boolean } | undefined;

interface UseDigitalWalletsParams {
  setIsSubmitting: (value: boolean) => void;
  onSupportChange: (value: boolean) => void;
  onPaymentCollected: () => Promise<CompletePurchaseSubmitResult>;
  onPurchaseRolledBack: () => void;
  onError: () => void;
}

export const useDigitalWallets = ({
  setIsSubmitting,
  onSupportChange,
  onPaymentCollected,
  onPurchaseRolledBack,
  onError
}: UseDigitalWalletsParams) => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    state: {
      paymentSummary: {
        isCardEligible,
        paymentType,
        purchasePaymentBreakdown,
        paymentSelection,
        consents
      }
    }
  } = usePaymentContext();

  const [isReady, setIsReady] = useState(false);
  const [isSupported, setIsSupported] = useState<boolean | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<DisplayError | null>(null);

  const trackDigitalWalletsEvent = useDigitalWalletsEventTracking();

  const digitalWallet = paymentSelection?.digitalWallet;
  const expressCheckoutElementOptions = getStripeExpressCheckoutElementOptions(
    digitalWallet,
    paymentType,
    purchasePaymentBreakdown
  );

  useEffect(() => {
    if (isReady) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsReady(true);
      setIsSupported(false);

      trackDigitalWalletsEvent('support_check_timeout', {
        digitalWallet,
        timeoutMs: supportCheckTimeoutMs
      });
    }, supportCheckTimeoutMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [digitalWallet, isReady, trackDigitalWalletsEvent]);

  useEffect(() => {
    if (isSupported !== undefined) {
      onSupportChange(isSupported);
    }
  }, [isSupported, onSupportChange]);

  const prepareDigitalWalletPayment = async (): Promise<PrepareDigitalWalletPaymentResult> => {
    if (!elements || !stripe) {
      return { error: digitalWalletErrors.somethingWentWrong };
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      trackDigitalWalletsEvent('submit_error', {
        digitalWallet,
        stripeErrorType: submitError.type,
        stripeErrorCode: submitError.code,
        stripeDeclineCode: submitError.decline_code
      });

      return { error: getErrorFromStripeErrorCode(submitError.code) };
    }

    const consentTypes = consents.map(({ consentType, consentSubType }) => ({
      consentType,
      consentSubType
    }));
    const { paymentId, amount, paymentIntentId, clientSecret } =
      await createDigitalWalletPaymentIntent(consentTypes);

    if (!paymentId || !paymentIntentId || !clientSecret) {
      return { error: digitalWalletErrors.somethingWentWrong };
    }

    trackDigitalWalletsEvent('payment_intent_created', { digitalWallet, paymentId, amount });

    if (amount.toFixed(2) !== purchasePaymentBreakdown.amountDue.toFixed(2)) {
      trackDigitalWalletsEvent('amount_mismatch', {
        digitalWallet,
        paymentId,
        createdAmount: amount,
        expectedAmount: purchasePaymentBreakdown.amountDue
      });

      return { error: digitalWalletErrors.somethingWentWrong };
    }

    return { paymentIntent: { paymentId, paymentIntentId, clientSecret } };
  };

  const confirmDigitalWalletPayment = async (
    stripe: Stripe,
    elements: StripeElements,
    { paymentId, paymentIntentId, clientSecret }: DigitalWalletPaymentIntent
  ): Promise<ConfirmDigitalWalletPaymentResult> => {
    const { paymentIntent, error: stripeError } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: window.location.href,
        expand: ['payment_method']
      },
      redirect: 'if_required'
    });

    const paymentMethod =
      typeof paymentIntent?.payment_method === 'object'
        ? paymentIntent.payment_method
        : stripeError?.payment_method;
    const isPrepaidCard = paymentMethod?.card?.funding === 'prepaid';

    if (stripeError?.code) {
      trackDigitalWalletsEvent('stripe_error', {
        digitalWallet,
        paymentId,
        stripeErrorCode: stripeError.code,
        stripeDeclineCode: stripeError.decline_code
      });
    }

    const { paymentCollectionSucceeded, purchaseRolledBack } = await completeDigitalWalletPayment({
      paymentId,
      paymentIntentId
    })
      .then(response => {
        trackDigitalWalletsEvent('payment_completion_completed', {
          digitalWallet,
          paymentId,
          paymentCollectionSucceeded: response.paymentCollectionSucceeded,
          purchaseRolledBack: response.purchaseRolledBack,
          paymentIntentStatus: paymentIntent?.status,
          hasStripeError: !!stripeError
        });

        return response;
      })
      .catch(() => {
        trackDigitalWalletsEvent('payment_completion_error', {
          digitalWallet,
          paymentId,
          stripeErrorCode: stripeError?.code,
          isPrepaidCard
        });

        return {
          paymentCollectionSucceeded: false,
          purchaseRolledBack: false
        };
      });

    if (paymentCollectionSucceeded) {
      return { status: 'PaymentCollected' };
    }

    if (purchaseRolledBack) {
      return { status: 'PurchaseRolledBack' };
    }

    if (isPrepaidCard) {
      return { status: 'PaymentFailed', error: digitalWalletErrors.prepaidCard };
    }

    return {
      status: 'PaymentFailed',
      error: stripeError?.code
        ? getErrorFromStripeErrorCode(stripeError.code)
        : digitalWalletErrors.somethingWentWrong
    };
  };

  const collectDigitalWalletPayment = async (
    event: StripeExpressCheckoutElementConfirmEvent
  ): Promise<CollectDigitalWalletPaymentResult> => {
    const { paymentIntent, error: preparePaymentIntentError } =
      await prepareDigitalWalletPayment().catch(() => ({
        error: digitalWalletErrors.somethingWentWrong,
        paymentIntent: undefined
      }));

    if (!stripe || !elements || !paymentIntent) {
      event.paymentFailed({ reason: 'fail' });
      setError(preparePaymentIntentError ?? digitalWalletErrors.somethingWentWrong);
      return;
    }

    const { status, error } = await confirmDigitalWalletPayment(stripe, elements, paymentIntent);

    if (status === 'PaymentCollected') {
      const { didNavigate } = await onPaymentCollected();

      // Keep the spinner visible while navigating
      return { keepSpinner: didNavigate };
    }
    if (status === 'PurchaseRolledBack') {
      onPurchaseRolledBack();
    }

    if (status === 'PaymentFailed') {
      setError(error);
    }
  };

  const onReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const availablePaymentMethods = event.availablePaymentMethods;
    const isApplePaySupported = availablePaymentMethods?.applePay ?? false;
    const isGooglePaySupported = availablePaymentMethods?.googlePay ?? false;

    const isSelectedWalletSupported =
      (digitalWallet === 'ApplePay' && isApplePaySupported) ||
      (digitalWallet === 'GooglePay' && isGooglePaySupported) ||
      (digitalWallet === undefined && (isApplePaySupported || isGooglePaySupported));

    if (!isReady) {
      setIsReady(true);
      setIsSupported(isSelectedWalletSupported);
    }

    trackDigitalWalletsEvent('load', {
      digitalWallet,
      availablePaymentMethods,
      isApplePaySupported,
      isGooglePaySupported,
      isSelectedWalletSupported
    });
  };

  const onLoadError = (event: { error: StripeError }) => {
    if (!isReady) {
      setIsReady(true);
      setIsSupported(false);
    }

    trackDigitalWalletsEvent('load_error', {
      digitalWallet,
      stripeErrorType: event.error.type,
      stripeErrorCode: event.error.code
    });
  };

  const onClick = (event: StripeExpressCheckoutElementClickEvent) => {
    event.resolve();
    setError(null);

    trackDigitalWalletsEvent('click', {
      digitalWallet,
      walletType: event.expressPaymentType
    });
  };

  const onCancel = () => {
    trackDigitalWalletsEvent('cancel', { digitalWallet });
  };

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    trackDigitalWalletsEvent('confirm', {
      digitalWallet,
      walletType: event.expressPaymentType
    });

    setError(null);
    setIsSaving(true);
    setIsSubmitting(true);

    const collectPaymentResult = await collectDigitalWalletPayment(event).catch(() => {
      onError();
    });

    if (collectPaymentResult?.keepSpinner) {
      return;
    }

    setIsSaving(false);
    setIsSubmitting(false);
  };

  return {
    isReady,
    isSupported,
    isSaving,
    error,
    isCardEligible,
    digitalWallet,
    expressCheckoutElementOptions,
    onReady,
    onLoadError,
    onClick,
    onCancel,
    onConfirm
  };
};
